/**
 * Column layouts for the design tabs that offer a Columns choice: presets 1–3
 * plus a Custom count of 4–12. Every surface stores the plain number and reads
 * it back through {@link clampColumnCount}, so the editor's live preview and the
 * display always agree on how many columns a design has.
 */
export const COLUMN_PRESETS = [1, 2, 3];
export const CUSTOM_COLUMNS_MIN = 4;
export const COLUMNS_MAX = 12;

/** A stored column count as rendered: a whole number 1–12; non-numeric → `fallback`. */
export function clampColumnCount(value: unknown, fallback = 1): number {
  const columns = Number(value);
  if (!Number.isFinite(columns)) return fallback;
  return Math.min(Math.max(Math.round(columns), 1), COLUMNS_MAX);
}

/**
 * Grid tracks for a card grid that centers its last row. From 3 columns up
 * every card spans 2 sub-columns (2 × columns tracks), which keeps full rows
 * identical to a plain grid while letting a short last row sit centered — see
 * {@link getCenteredCardPlacement}.
 */
export function getCenteredCardTemplateColumns(columns: unknown): number {
  const cols = clampColumnCount(columns);
  return cols >= 3 ? cols * 2 : cols;
}

/**
 * Per-card grid placement that centers a trailing partial row instead of
 * leaving it hanging to one side. Symmetric, so it reads the same in an RTL
 * container (track 1 is the right-hand edge there). `gapPx` is the grid's
 * column gap.
 *
 *  • 2 columns: an odd count leaves the last card alone → span both columns
 *    but keep it one column wide and centered.
 *  • 3+ columns: on the 2 × columns sub-grid, full-row cards take their
 *    natural slot; the `remainder` cards of the last row span 2 × remainder
 *    tracks, leaving (columns − remainder) free tracks on each side.
 */
export function getCenteredCardPlacement(
  columns: unknown,
  index: number,
  total: number,
  gapPx: number,
): Record<string, string> {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(index) || index < 0 || index >= total) return {};
  const cols = clampColumnCount(columns);

  if (cols === 2) {
    if (total % 2 === 1 && index === total - 1) {
      return { gridColumn: '1 / -1', justifySelf: 'center', width: `calc((100% - ${Number(gapPx) || 0}px) / 2)` };
    }
    return {};
  }
  if (cols < 3) return {};

  const remainder = total % cols;
  const fullCount = total - remainder; // cards filling complete rows
  if (index < fullCount) {
    return { gridColumn: `${(index % cols) * 2 + 1} / span 2`, gridRow: String(Math.floor(index / cols) + 1) };
  }
  const start = cols - remainder + 1 + (index - fullCount) * 2;
  return { gridColumn: `${start} / span 2`, gridRow: String(fullCount / cols + 1) };
}

/**
 * Live-preview floor for a Custom count (4+). Editor previews are narrow side
 * panels, so instead of crushing each column to a few pixels the grid keeps
 * every column at least `columnPx` wide and its panel scrolls sideways.
 * Undefined for presets 1–3, whose previews are unchanged.
 *
 * Apply it as the grid CONTAINER's min-width, not as a track minimum: the box
 * itself then widens into the scrollable area, whereas tracks overflowing a
 * dir="rtl" box spill past its left edge, where nothing can scroll to them.
 */
export function getPreviewGridMinWidth(columns: unknown, columnPx: number, gapPx: number): string | undefined {
  const cols = clampColumnCount(columns);
  if (cols < CUSTOM_COLUMNS_MIN) return undefined;
  return `${cols * columnPx + (cols - 1) * (Number(gapPx) || 0)}px`;
}
